import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const defaultValues = [
  {
    title: "Prøve- og karakterfri",
    desc: "Vi erstatter karakterræs og standardiserede tests med personlig feedback, faglig fordybelse og reel lærelyst.",
    icon: "fa-solid fa-graduation-cap"
  },
  {
    title: "Tryghed",
    desc: "Når et barn føler sig trygt og anerkendt, åbner nysgerrigheden og lysten til at lære sig helt af sig selv.",
    icon: "fa-regular fa-heart"
  },
  {
    title: "Faglighed",
    desc: "Vi stiller krav og udfordrer eleverne på deres niveau med differentieret støtte og engagerede lærere.",
    icon: "fa-regular fa-lightbulb"
  },
  {
    title: "Nærhed",
    desc: "Børnene forsvinder ikke i mængden. Vi kender hver elevs styrker, udfordringer og personlighed.",
    icon: "fa-regular fa-face-smile"
  },
  {
    title: "Fællesskab",
    desc: "De store hjælper de små til morgensang, temauger og i frikvartererne. Vi er ét fælles skoleliv.",
    icon: "fa-regular fa-handshake"
  },
  {
    title: "Små klasser",
    desc: "Mindre klassestørrelser giver læreren tid til at lytte, støtte og give den enkelte elev fuld opmærksomhed.",
    icon: "fa-regular fa-users"
  },
  {
    title: "Trivsel",
    desc: "Trivsel og glæde er ikke et biprodukt, men den direkte forudsætning for god undervisning og social udvikling.",
    icon: "fa-regular fa-sun"
  }
];

export default function ValuesSection() {
  const [showAll, setShowAll] = useState(false);
  const [valuesList, setValuesList] = useState(defaultValues);

  useEffect(() => {
    fetch('/_api/public/content/vaerdier')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
          const mapped = sorted.map((v) => ({
            title: v.titel || v.title,
            desc: v.beskrivelse || v.desc,
            icon: v.ikon || v.icon || "fa-regular fa-heart"
          }));
          setValuesList(mapped);
        }
      })
      .catch(() => {});
  }, []);

  const displayedValues = showAll ? valuesList : valuesList.slice(0, 4);

  return (
    <section id="vaerdier" className="py-20 lg:py-28 bg-[#eef4e6] relative overflow-hidden scroll-mt-20">
      
      {/* Decorative subtle background accents */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/60 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#88A68E]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Editorial Layout: Left text intro + Right Value Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Text Feature without image */}
          <div className="lg:col-span-5 flex flex-col justify-between h-full space-y-6">
            <div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1D2923] mb-4 font-serif-heading">
                En skole med hjerte og plads til at vokse
              </h3>
              <p className="text-sm sm:text-base text-[#5A6961] leading-relaxed mb-4">
                På Odder Lille Friskole er skoledagen fyldt med nærvær, faglig nysgerrighed og gensidig respekt. Vi er en prøve- og karakterfri friskole, hvor børnene mødes som hele mennesker i et trygt miljø med tæt kontakt til engagerede voksne.
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#264A35]/10 text-[#264A35] text-xs font-semibold">
                <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
                <span>Prøve- og karakterfri fra 0. til 9. klasse</span>
              </div>
            </div>

            <div className="pt-6 border-t border-[#d9e5d4] flex items-center justify-start">
              <Link
                to="/side/vaerdigrundlag"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#264A35] hover:text-black transition-colors"
              >
                <span>Læs værdigrundlag</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Right Column: Values Grid (4 initial + expand) */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {displayedValues.map((val) => (
                <div
                  key={val.title}
                  className="bg-white rounded-2xl p-6 border border-[#d9e5d4] flex flex-col justify-between shadow-xs transition-all duration-300"
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold text-[#1D2923] font-serif-heading">
                        {val.title}
                      </span>
                      <div className="text-[#264A35] flex items-center justify-center">
                        <i className={`${val.icon} text-xl`} aria-hidden="true" />
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-[#5A6961] leading-relaxed">
                      {val.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {valuesList.length > 4 && (
              <div className="mt-5 flex justify-center sm:justify-start">
                <button
                  type="button"
                  onClick={() => setShowAll(!showAll)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] shadow-xs hover:text-black transition-all cursor-pointer"
                >
                  <span>{showAll ? 'Vis færre værdier' : 'Se alle værdier'}</span>
                  <i className={`fa-solid ${showAll ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

        </div>

      </div>
    </section>
  );
}
